import React from 'react'
import PropTypes from 'prop-types'

import invalid from '../../../assets/images/invalid.svg'

/**
 * TextArea component renders a customizable text area input field.
 * It supports both "add" and "edit" modes, applies validation styles, and integrates with `react-hook-form`.
 *
 * @module form/TextArea
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} props.field - Configuration object for the text area field.
 * @param {string} props.field.name - Unique name of the field (used for form handling).
 * @param {string} props.field.placeholder - Placeholder text for the text area.
 * @param {number} props.field.size - Column size for grid layout (e.g., Bootstrap grid).
 * @param {string} props.field.type - The type of input (typically "text").
 * @param {boolean} [props.field.hidden=false] - Whether the field should be hidden.
 * @param {boolean} props.isAdd - Indicates whether the form is in "add" mode.
 * @param {Object} [props.defaultData={}] - Default data object used to pre-fill values in "edit" mode.
 * @param {Object} props.errors - Validation errors object from `react-hook-form`.
 * @param {Function} props.register - Function to register the text area input (provided by `react-hook-form`).
 *
 * @example
 * const field = {
 *   name: 'description',
 *   placeholder: 'Enter description',
 *   size: 6,
 *   hidden: false,
 *   type: 'text'
 * };
 * const isAdd = true;
 * const defaultData = { description: 'Existing text' };
 * const errors = {};
 * const register = () => {};
 *
 * <TextArea
 *   field={field}
 *   isAdd={isAdd}
 *   defaultData={defaultData}
 *   errors={errors}
 *   register={register}
 * />
 *
 * @returns {JSX.Element} The rendered TextArea component.
 */
export const TextArea = ({ field, errors, register, isAdd, defaultData }) => {
  try {
    return (
      <div
        className={`trtui-col-md-${field.size}`}
        style={{ display: field.hidden ? 'none' : 'block' }}
      >
        <label className="trtui-form-label">{field.placeholder}</label>
        <textarea
          type={field.type}
          className={`trtui-form-control ${errors[field.name] && 'trtui-is-invalid'}`}
          placeholder={field.placeholder}
          {...register(field.name)}
          style={{
            paddingRight: 'calc(1.5em + 1rem)',
            backgroundImage: errors[field.name] ? `url("${invalid}")` : 'none',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right calc(0.375em + 0.25rem) center',
            backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
          }}
        >
          {!isAdd && defaultData[field.name]}
        </textarea>
        {errors[field.name] && (
          <div className="trtui-invalid-feedback">{errors[field.name].message}</div>
        )}
      </div>
    )
  } catch (err) {
    return <div>Error</div>
  }
}

TextArea.propTypes = {
  /**
   * Configuration object for the text area field.
   */
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    size: PropTypes.number,
    type: PropTypes.string,
    hidden: PropTypes.bool,
  }).isRequired,

  /**
   * Indicates whether the form is in "add" mode.
   */
  isAdd: PropTypes.bool.isRequired,

  /**
   * Default data object containing pre-filled values (used in edit mode).
   */
  defaultData: PropTypes.object,

  /**
   * Object containing validation errors from `react-hook-form`.
   */
  errors: PropTypes.object.isRequired,

  /**
   * Function to register the text area input with `react-hook-form`.
   */
  register: PropTypes.func.isRequired,
}
